import update from 'immutability-helper';
import { IParameterSession } from './ParameterInterface';

const parameterState: IParameterSession = {
  properties: {
    '1.1': {
      parameterSession: null,
    },
  },
  savedParameters: [],
};

const ParameterReducer = (
  state: IParameterSession = parameterState,
  action: any,
): IParameterSession => {
  switch (action.type) {
    case 'SET_PARAMETERS': {
      const propKey: string = action.payload.propKey;
      const name: string = action.payload.name;
      const datasetId: string = action.payload.datasetId;
      const parameterId: string = action.payload.parameterId;

      const newState = update(state, {
        properties: {
          $set: {
            [propKey]: {
              parameterSession: {
                parameterId: parameterId,
                name: name,
                datasetId: datasetId,
                columns: {
                  dataType: '',
                  displayType: '',
                  defaultValue: '',
                  fieldName: '',
                  tableId: '',
                  aggregationType: 'none',
                  has_calculation: false,
                  originalDataType: '',
                },
                loadFromColumn: false,
                loadDefaultValue: false,
                isPreview: false,
                mode: 'add',
                showParameter: false,
                isRange: '',
              },
            },
          },
        },
        savedParameters: {
          $set: state.savedParameters,
        },
      });

      return newState;
    }

    case 'ADD_PARAMETER': {
      const propKey: string = action.payload.propKey;
      const currentParameterSession = state.properties[propKey]?.parameterSession;

      if (!currentParameterSession) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            $set: {
              parameterSession: null,
            },
          },
        },
        savedParameters: {
          $push: [currentParameterSession],
        },
      });

      return newState;
    }

    case 'PREVIEW_PARAMETER': {
      const propKey: string = action.payload.propKey;
      const preview: boolean = action.payload.preview;
      const isRange = action.payload.isRange;
      const name = action.payload?.name;
      const columns = action.payload?.columns;
      const values = action.payload?.values || [];
      const minValue = action.payload?.minValue || 0;
      const maxValue = action.payload?.maxValue || 0;
      const stepValue = action.payload?.stepValue || 0;
      const loadFromColumn = action.payload?.loadFromColumn || false;
      const loadDefaultValue = action.payload?.loadDefaultValue || false;
      const formatOptions = action.payload?.formatOptions || {
        formatValue: 'Number',
        currencySymbol: '',
        enableRounding: false,
        roundingDigits: 1,
        numberSeparator: 'None',
      };

      return update(state, {
        properties: {
          $set: {
            [propKey]: {
              parameterSession: {
                parameterId: state.properties[propKey]?.parameterSession?.parameterId || '',
                name: name || state.properties[propKey]?.parameterSession?.name,
                datasetId: state.properties[propKey]?.parameterSession?.datasetId || '',
                values: values,
                minValue: minValue,
                maxValue: maxValue,
                stepValue: stepValue,
                columns: {
                  dataType: columns?.dataType || '',
                  displayType: columns?.displayType || '',
                  defaultValue: columns?.defaultValue || '',
                  fieldName: columns?.fieldName || '',
                  tableId: columns?.tableId || '',
                  aggregationType: columns?.aggregationType || 'none',
                  defaultValueMax: columns?.defaultValueMax || '',
                  defaultValueMin: columns?.defaultValueMin || '',
                  calculatedFieldId: columns?.calculatedFieldId || '',
                  has_calculation: columns?.has_calculation || false,
                  originalDataType: columns?.originalDataType || '',
                },
                loadFromColumn: loadFromColumn,
                loadDefaultValue: loadDefaultValue,
                isPreview: preview,
                mode: state.properties[propKey]?.parameterSession?.mode || 'add',
                showParameter: state.properties[propKey]?.parameterSession?.showParameter || false,
                formatOptions: formatOptions,
                isRange: isRange || '',
                chartFilter: state.properties[propKey]?.parameterSession?.chartFilter || [],
                userSelection: state.properties[propKey]?.parameterSession?.userSelection || '',
                calculationId: state.properties[propKey]?.parameterSession?.calculationId || [],
              },
            },
          },
        },
      });
    }

    case 'LOAD_SAVED_PARAMETER': {
      const propKey: string = action.payload.propKey;
      const parameterId: string = action.payload.parameterId;
      const savedParameter = state.savedParameters.filter(
        (savedParameter) => savedParameter.parameterId === parameterId,
      );

      const newState = update(state, {
        properties: {
          [propKey]: {
            $set: {
              parameterSession: {
                ...savedParameter[0],
                mode: 'edit',
              },
            },
          },
        },
      });

      return newState;
    }

    case 'UPDATE_PARAMETER': {
      const propKey: string = action.payload.propKey;
      const parameterId: string = action.payload.parameterId;
      const currentParameterSession = state.properties[propKey]?.parameterSession;

      if (!currentParameterSession) return state;
      currentParameterSession.mode = 'add';

      const parameterIndex = state.savedParameters.findIndex(
        (savedParameter) => savedParameter.parameterId === parameterId,
      );

      if (parameterIndex === -1) return state;

      const newState = update(state, {
        properties: {
          [propKey]: {
            $set: {
              parameterSession: null,
            },
          },
        },
        savedParameters: {
          [parameterIndex]: {
            $set: currentParameterSession,
          },
        },
      });

      return newState;
    }

    case 'DELETE_PARAMETER': {
      const propKey: string = action.payload.propKey;
      const parameterId: string = action.payload.parameterId;

      const savedParameter = state.savedParameters.filter(
        (savedParameter) => savedParameter.parameterId !== parameterId,
      );

      const newState = update(state, {
        properties: {
          [propKey]: {
            $set: {
              parameterSession: null,
            },
          },
        },
        savedParameters: {
          $set: savedParameter,
        },
      });

      return newState;
    }

    case 'ADD_REMOVE_PARAMETER_TO_CHART': {
      const propKey: string = action.payload.propKey;
      const parameterId: string = action.payload.parameterId;

      const parameterIndex = state.savedParameters.findIndex(
        (savedParameter) => savedParameter.parameterId === parameterId,
      );

      if (parameterIndex === -1) {
        return state;
      }

      const updateParameter = { ...state.savedParameters[parameterIndex] };
      updateParameter.showParameter = !updateParameter.showParameter;

      const newState = update(state, {
        properties: {
          [propKey]: {
            $set: {
              parameterSession: null,
            },
          },
        },
        savedParameters: {
          [parameterIndex]: {
            $set: updateParameter,
          },
        },
      });

      return newState;
    }

    case 'SYNC_PARAMETER_DATA': {
      const parameterId: string = action.payload.parameterId;
      const newValues = action.payload.values;
      const minValues = action.payload.minValue;
      const maxValue = action.payload.maxValue;
      const stepValue = action.payload.stepValue;
      const defaultValue = action.payload?.defaultValue;

      const savedParameter = state.savedParameters.filter(
        (savedParameter) => savedParameter.parameterId !== parameterId,
      );

      const currentSavedParameter = state.savedParameters.find(
        (savedParameter) => savedParameter.parameterId === parameterId,
      );

      if (!currentSavedParameter) return state;
      currentSavedParameter.values = newValues;
      currentSavedParameter.minValue = minValues;
      currentSavedParameter.maxValue = maxValue;
      currentSavedParameter.stepValue = stepValue;
      currentSavedParameter.columns.defaultValue = defaultValue;

      const newState = update(state, {
        savedParameters: {
          $set: [...savedParameter, currentSavedParameter],
        },
      });

      return newState;
    }

    case 'UPDATE_PARAMETER_USER_SELECTION': {
      const parameterId: string = action.payload.parameterId;
      const userSelection: any = action.payload.userSelection;

      const parameterIndex = state.savedParameters.findIndex(
        (savedParameter) => savedParameter.parameterId === parameterId,
      );

      if (parameterIndex === -1) return state;

      const updatedParameter = {
        ...state.savedParameters[parameterIndex],
        userSelection: userSelection,
      };

      const newState = update(state, {
        savedParameters: {
          [parameterIndex]: {
            $set: updatedParameter,
          },
        },
      });

      return newState;
    }

    case 'RESET_PARAMETER_SESSION': {
      const propKey: string = action.payload.propKey;

      const newState = update(state, {
        properties: {
          [propKey]: {
            $set: {
              parameterSession: null,
            },
          },
        },
        savedParameters: {
          $set: [...(state.savedParameters || [])],
        },
      });

      return newState;
    }

    case 'LOAD_ALL_PARAMETERS': {
      const parameters = action.payload.parameters || parameterState;

      return parameters;
    }

    // PARAMETER INTO FILTERS

    case 'SET_PARAMETER_INTO_FILTERS': {
      const propKey: string = action.payload.propKey;
      const parameterId: string = action.payload.parameterId;

      const uId: string = action.payload.uId;
      const itemIndex: number = action.payload.itemIndex;
      const currentChartAxesName: string = action.payload.currentChartAxesName;

      const savedParameter = state.savedParameters.filter(
        (savedParameter) => savedParameter.parameterId !== parameterId,
      );

      const currentSavedParameter = state.savedParameters.find(
        (savedParameter) => savedParameter.parameterId === parameterId,
      );

      if (!currentSavedParameter) {
        return state;
      }

      if (!currentSavedParameter.chartFilter) currentSavedParameter.chartFilter = [];
      const filterObject = {
        propKey: propKey,
        chartFilterId: uId,
        currentChartAxesName: currentChartAxesName,
        itemIndex: itemIndex,
      };

      // Only add if chartFilterId is not already present
      // const isAlreadyPresent = currentSavedParameter.chartFilter.some(
      //   (filter: any) => filter.chartFilterId === filterObject.chartFilterId,
      // );
      const isAlreadyPresent = currentSavedParameter.chartFilter.some((filter: any) => {
        const match = filter.chartFilterId === filterObject.chartFilterId;
        if (match && !filter.propKey) {
          filter.propKey = propKey;
        }
        return match;
      });

      if (!isAlreadyPresent) {
        currentSavedParameter.chartFilter.push(filterObject);
      }

      const newState = update(state, {
        properties: {
          [propKey]: {
            $set: {
              parameterSession: null,
            },
          },
        },
        savedParameters: {
          $set: [...savedParameter, currentSavedParameter],
        },
      });

      return newState;
    }

    case 'DELETE_FILTER_PARAMETER_FROM': {
      const propKey: string = action.payload.propKey;
      const uId: string = action.payload.uId;
      const currentChartAxesName: string = action.payload.currentChartAxesName;
      const allSavedParameters = state.savedParameters.map((savedParameter: any) => {
        if (savedParameter.chartFilter) {
          savedParameter.chartFilter = savedParameter.chartFilter.filter(
            (filter: any) =>
              !(
                filter.chartFilterId === uId && filter.currentChartAxesName === currentChartAxesName
              ),
          );
        }
        return savedParameter;
      });
      const newState = update(state, {
        properties: {
          [propKey]: {
            $set: {
              parameterSession: null,
            },
          },
        },
        savedParameters: {
          $set: allSavedParameters,
        },
      });

      return newState;
    }

    case 'REORDER_PARAMETERIZED_CHARTS': {
      const propKey: string = action.payload.propKey;
      const parameterId: string = action.payload.parameterId;
      const chartFilter = action.payload.chartFilter;

      const savedParameter = state.savedParameters.filter(
        (savedParameter) => savedParameter.parameterId !== parameterId,
      );

      const currentSavedParameter = state.savedParameters.find(
        (savedParameter) => savedParameter.parameterId === parameterId,
      );

      if (!currentSavedParameter) {
        return state;
      }

      if (!currentSavedParameter.chartFilter) currentSavedParameter.chartFilter = [];
      currentSavedParameter.chartFilter = chartFilter;

      const newState = update(state, {
        properties: {
          [propKey]: {
            $set: {
              parameterSession: null,
            },
          },
        },
        savedParameters: {
          $set: [...savedParameter, currentSavedParameter],
        },
      });

      return newState;
    }

    // CALCULATION ID

    case 'SET_CALCULATION_ID': {
      const calculationId: string = action.payload.calculationId;
      const parameterId: string = action.payload.parameterId;

      const savedParameter = state.savedParameters.filter(
        (savedParameter) => savedParameter.parameterId !== parameterId,
      );

      const targetParameter = state.savedParameters.find(
        (savedParameter) => savedParameter.parameterId === parameterId,
      );

      if (!targetParameter) return state;

      const isAlreadyPresent = targetParameter.calculationId?.some(
        (id: string) => id === calculationId,
      );
      if (isAlreadyPresent) return state;

      if (!targetParameter.calculationId) targetParameter.calculationId = [];
      targetParameter.calculationId.push(calculationId);

      const newState = update(state, {
        savedParameters: {
          $set: [...savedParameter, targetParameter],
        },
      });

      return newState;
    }

    case 'REMOVE_CALCULATION_ID': {
      const calculationId: string = action.payload.calculationId;
      const parameterId: string = action.payload.parameterId;

      const savedParameter = state.savedParameters.filter(
        (savedParameter) => savedParameter.parameterId !== parameterId,
      );

      const targetParameter = state.savedParameters.find(
        (savedParameter) => savedParameter.parameterId === parameterId,
      );

      if (!targetParameter) return state;

      const newCalculationIds = targetParameter.calculationId?.filter(
        (id: string) => id !== calculationId,
      );
      targetParameter.calculationId = newCalculationIds;

      const newState = update(state, {
        savedParameters: {
          $set: [...savedParameter, targetParameter],
        },
      });

      return newState;
    }

    // case 'HAS_CALCULATION': {
    //   const has_calculation: boolean = action.payload.has_calculation;
    //   const propKey: string = action.payload.propKey;
    //   const currentParameterSession = state.properties[propKey]?.parameterSession;

    //   if (!currentParameterSession) return state;

    //   currentParameterSession.has_calculation = has_calculation;

    //   const newState = update(state, {
    //     properties: {
    //       [propKey]: {
    //         $set: {
    //           parameterSession: currentParameterSession,
    //         },
    //       },
    //     },
    //   });

    //   return newState;
    // }

    case 'CLEAR_PARAMETER_SESSION':
      return parameterState;

    default:
      return state;
  }
};

export default ParameterReducer;
